import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore, selectIsAuthed } from '@/stores/auth';

interface RouteGuardProps {
  children: ReactNode;
}

/**
 * Gates the authed routes. Phase 0 wiring:
 *   - no token → /login
 *   - requires_2fa flag set → /login/2fa
 *
 * Phase 8 will tighten: enforce TOTP enrollment on first login, force a
 * fresh TOTP challenge per session, and check `role === 'admin'` against the
 * `/auth/me` response.
 */
export function RouteGuard({ children }: RouteGuardProps) {
  const isAuthed = useAuthStore(selectIsAuthed);
  const requires2fa = useAuthStore((s) => s.requires2fa);
  const totpChallenged = useAuthStore((s) => s.totpChallenged);
  const location = useLocation();

  if (!isAuthed) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (requires2fa && !totpChallenged) {
    return <Navigate to="/login/2fa" replace />;
  }

  return <>{children}</>;
}
